import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import 'mathlive';
import { FiBold, FiItalic, FiUnderline, FiMinus, FiPlus, FiChevronDown } from 'react-icons/fi';
import {
  MdUndo,
  MdRedo,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatAlignLeft,
  MdFormatAlignCenter,
  MdFormatAlignRight,
  MdGridOn,
  MdFormatColorText,
} from 'react-icons/md';
import { MdImage } from 'react-icons/md';
import { useColorContext } from '../../../context/colorContext';

const MATH_UI_STYLE_ID = 'plynote-math-ui';
if (typeof document !== 'undefined' && !document.getElementById(MATH_UI_STYLE_ID)) {
  const styleTag = document.createElement('style');
  styleTag.id = MATH_UI_STYLE_ID;
  styleTag.textContent = `
    .plynote-math-scroll {
      scrollbar-width: auto;
      scrollbar-color: var(--plynote-math-thumb-idle, rgba(25, 25, 25, 0.96)) transparent;
    }
    .plynote-math-scroll::-webkit-scrollbar {
      width: 14px;
      height: 14px;
    }
    .plynote-math-scroll::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 999px;
    }
    .plynote-math-scroll::-webkit-scrollbar-thumb {
      background: var(--plynote-math-thumb-idle, rgba(25, 25, 25, 0.96));
      border-radius: 999px;
      border: 2px solid transparent;
      background-clip: content-box;
      transition: background 0.15s ease;
    }
    .plynote-math-scroll:hover::-webkit-scrollbar-thumb {
      background: var(--plynote-math-thumb-active, rgba(59, 130, 246, 0.9));
      background-clip: content-box;
    }
    .plynote-math-field {
      width: 100%;
      min-height: 96px;
      border-radius: 8px;
      border: 1px solid var(--plynote-math-field-border, rgba(255, 255, 255, 0.22));
      background: var(--plynote-math-field-bg, rgba(0, 0, 0, 0.22));
      color: var(--plynote-math-field-text, #F0F0F0);
      padding: 8px 10px;
      box-sizing: border-box;
      font-size: 20px;
    }
    .plynote-math-field::part(virtual-keyboard-toggle),
    .plynote-math-field .ML__virtual-keyboard-toggle {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }

    .plynote-toolbar-scroll {
      scrollbar-gutter: stable;
    }

    .plynote-toolbar-scroll::-webkit-scrollbar {
      width: 10px;
      height: 10px;
    }

    .plynote-toolbar-scroll::-webkit-scrollbar:horizontal {
      height: 10px;
    }

    .plynote-toolbar-scroll::-webkit-scrollbar:vertical {
      width: 0;
      height: 0;
    }
  `;
  document.head.appendChild(styleTag);
}

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ─── Options de style de paragraphe ──────────────────────────────────────────
const STYLE_OPTIONS = [
  { label: 'Normal',   value: 'paragraph' },
  { label: 'Titre 1',  value: 'h1' },
  { label: 'Titre 2',  value: 'h2' },
  { label: 'Titre 3',  value: 'h3' },
  { label: 'Titre 4',  value: 'h4' },
  { label: 'Titre 5',  value: 'h5' },
  { label: 'Titre 6',  value: 'h6' },
];

const FONT_FAMILY_OPTIONS = [
  { label: 'Inter', value: 'Inter, "Segoe UI", "SF Pro Text", system-ui, sans-serif' },
  { label: 'Serif', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Sans moderne', value: '"Segoe UI", "Trebuchet MS", Arial, sans-serif' },
  { label: 'Sans net', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Monospace', value: '"Courier New", Courier, monospace' },
];

const FONT_SIZE_MIN = 10;
const FONT_SIZE_MAX = 96;
const MATH_FONT_SIZE_MIN = 12;
const MATH_FONT_SIZE_MAX = 120;

const MATH_SYMBOL_GROUPS = [
  {
    title: 'Operations',
    items: [
      { display: 'a/b', latex: '\\frac{a}{b}' },
      { display: 'x²', latex: 'x^{2}' },
      { display: 'xₙ', latex: 'x_{n}' },
      { display: '√x', latex: '\\sqrt{x}' },
      { display: '∛x', latex: '\\sqrt[3]{x}' },
      { display: 'ⁿ√x', latex: '\\sqrt[n]{x}' },
      { display: 'x̄', latex: '\\bar{x}' },
      { display: 'x̂', latex: '\\hat{x}' },
      { display: '|x|', latex: '\\left|x\\right|' },
      { display: '( )', latex: '\\left( \\; \\right)' },
    ],
  },
  {
    title: 'Trig',
    items: [
      { display: 'sin', latex: '\\sin' },
      { display: 'cos', latex: '\\cos' },
      { display: 'tan', latex: '\\tan' },
      { display: 'sec', latex: '\\sec' },
      { display: 'csc', latex: '\\csc' },
      { display: 'cot', latex: '\\cot' },
      { display: 'sin⁻¹', latex: '\\sin^{-1}' },
      { display: 'cos⁻¹', latex: '\\cos^{-1}' },
      { display: 'tan⁻¹', latex: '\\tan^{-1}' },
      { display: 'sinh', latex: '\\sinh' },
      { display: 'cosh', latex: '\\cosh' },
      { display: 'tanh', latex: '\\tanh' },
    ],
  },
  {
    title: 'Calculus',
    items: [
      { display: '∑', latex: '\\sum' },
      { display: '∏', latex: '\\prod' },
      { display: '∫', latex: '\\int' },
      { display: '∬', latex: '\\iint' },
      { display: '∮', latex: '\\oint' },
      { display: 'lim', latex: '\\lim' },
      { display: '∂', latex: '\\partial' },
      { display: '∇', latex: '\\nabla' },
      { display: 'd/dx', latex: '\\frac{d}{dx}' },
      { display: 'ln', latex: '\\ln' },
      { display: 'log', latex: '\\log' },
      { display: 'exp', latex: '\\exp' },
    ],
  },
  {
    title: 'Greek',
    items: [
      { display: 'α', latex: '\\alpha' },
      { display: 'β', latex: '\\beta' },
      { display: 'γ', latex: '\\gamma' },
      { display: 'δ', latex: '\\delta' },
      { display: 'ε', latex: '\\epsilon' },
      { display: 'ζ', latex: '\\zeta' },
      { display: 'η', latex: '\\eta' },
      { display: 'θ', latex: '\\theta' },
      { display: 'λ', latex: '\\lambda' },
      { display: 'μ', latex: '\\mu' },
      { display: 'π', latex: '\\pi' },
      { display: 'ρ', latex: '\\rho' },
      { display: 'σ', latex: '\\sigma' },
      { display: 'τ', latex: '\\tau' },
      { display: 'φ', latex: '\\phi' },
      { display: 'ω', latex: '\\omega' },
      { display: 'Δ', latex: '\\Delta' },
      { display: 'Σ', latex: '\\Sigma' },
      { display: 'Ω', latex: '\\Omega' },
      { display: 'Φ', latex: '\\Phi' },
      { display: 'Π', latex: '\\Pi' },
      { display: '∞', latex: '\\infty' },
      { display: 'ℝ', latex: '\\mathbb{R}' },
      { display: 'ℤ', latex: '\\mathbb{Z}' },
    ],
  },
  {
    title: 'Operators',
    items: [
      { display: '±', latex: '\\pm' },
      { display: '∓', latex: '\\mp' },
      { display: '×', latex: '\\times' },
      { display: '÷', latex: '\\div' },
      { display: '·', latex: '\\cdot' },
      { display: '∘', latex: '\\circ' },
      { display: '<', latex: '<' },
      { display: '>', latex: '>' },
      { display: '≤', latex: '\\leq' },
      { display: '≥', latex: '\\geq' },
      { display: '≠', latex: '\\neq' },
      { display: '≈', latex: '\\approx' },
      { display: '≡', latex: '\\equiv' },
      { display: '∝', latex: '\\propto' },
      { display: '∈', latex: '\\in' },
      { display: '∉', latex: '\\notin' },
      { display: '⊂', latex: '\\subset' },
      { display: '⊆', latex: '\\subseteq' },
      { display: '∪', latex: '\\cup' },
      { display: '∩', latex: '\\cap' },
      { display: '∅', latex: '\\emptyset' },
      { display: '→', latex: '\\to' },
      { display: '⇒', latex: '\\Rightarrow' },
      { display: '⇔', latex: '\\Leftrightarrow' },
    ],
  },
];

// ─── Bouton de toolbar générique ─────────────────────────────────────────────
const ToolbarButton = ({ onClick, active, disabled, title, children, extraStyle }) => {
  const [hovered, setHovered] = useState(false);
  const { textPrimary, textSecondary, activeAccent, bgSecondary, bgTertiary } = useColorContext();
  const safeTextPrimary = toSafeHex(textPrimary, '#F0F0F0');
  const safeTextSecondary = toSafeHex(textSecondary, '#BDBDBD');
  const safeAccent = toSafeHex(activeAccent, '#3B82F6');
  const safeBgSecondary = toSafeHex(bgSecondary, '#191919');
  const safeBgTertiary = toSafeHex(bgTertiary, '#1E1E1E');

  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        padding: 0,
        border: 'none',
        borderRadius: 6,
        background: active
          ? hexToRgba(safeTextPrimary, 0.14, safeTextPrimary)
          : hovered
            ? hexToRgba(safeTextPrimary, 0.1, safeTextPrimary)
            : 'transparent',
        color: active ? safeAccent : disabled ? hexToRgba(safeTextSecondary, 0.42, safeTextSecondary) : safeTextSecondary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        outline: active ? `1px solid ${hexToRgba(safeAccent, 0.45, safeAccent)}` : 'none',
        transition: 'background 0.13s, color 0.13s',
        flexShrink: 0,
        boxShadow: 'none',
        ...extraStyle,
      }}
    >
      {children}
    </button>
  );
};

// ─── Séparateur vertical ──────────────────────────────────────────────────────
const Sep = () => (
  <SepInner />
);

const SepInner = () => {
  const { textPrimary } = useColorContext();
  const safeTextPrimary = toSafeHex(textPrimary, '#F0F0F0');
  return (
    <div style={{
      width: 1,
      height: 20,
      background: hexToRgba(safeTextPrimary, 0.14, safeTextPrimary),
      margin: '0 6px',
      flexShrink: 0,
    }} />
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────
const TextEditorToolsBar = ({
  editor,
  className,
  style,
  bgColor,
  height = '50px',
  // ── Tailles personnalisables ──
  buttonWidth = 30,
  buttonHeight = 30,
  buttonIconSize = 15,
  buttonIconSizeLarge = 18,
  separatorWidth = 1,
  separatorHeight = 20,
  gap = 6,
  paddingHorizontal = 16,
  paddingVertical = 10,
  selectMinWidth = 90,
  selectFontSize = 12,
  toolbarWidth = '100%',
}) => {
  const {
    bgSecondary,
    bgTertiary,
    textPrimary,
    textSecondary,
    textTertiary,
    activeAccent,
    varkColors,
    functionalColors,
  } = useColorContext();

  const safeBgSecondary = toSafeHex(bgSecondary, '#191919');
  const safeBgTertiary = toSafeHex(bgTertiary, '#1E1E1E');
  const safeTextPrimary = toSafeHex(textPrimary, '#F0F0F0');
  const safeTextSecondary = toSafeHex(textSecondary, '#BDBDBD');
  const safeTextTertiary = safeTextPrimary;
  const safeAccent = toSafeHex(activeAccent, '#3B82F6');
  const safeVarkVisual = toSafeHex(varkColors?.visual, safeAccent);
  const safeVarkAuditory = toSafeHex(varkColors?.auditory, safeAccent);
  const safeVarkReading = toSafeHex(varkColors?.reading, safeAccent);
  const safeVarkKinesthetic = toSafeHex(varkColors?.kinesthetic, safeAccent);
  const safeSuccess = toSafeHex(functionalColors?.success, safeVarkReading);
  const safeWarning = toSafeHex(functionalColors?.warning, safeVarkKinesthetic);
  const safeError = toSafeHex(functionalColors?.error, safeVarkVisual);

  const toolbarBg = bgColor || hexToRgba(safeBgSecondary, 0.97, safeBgSecondary);
  const premiumPanelGradient = hexToRgba(safeTextPrimary, 0.1, safeTextPrimary);
  const premiumPanelBorder = `1px solid ${hexToRgba(safeAccent, 0.42, safeAccent)}`;
  const premiumPanelShadow = 'none';

  const dropdownTriggerBaseStyle = {
    width: '100%',
    background: hexToRgba(safeTextPrimary, 0.1, safeTextPrimary),
    border: 'none',
    borderRadius: 11,
    color: safeTextTertiary,
    fontSize: selectFontSize,
    fontWeight: 600,
    padding: '4px 34px 4px 12px',
    cursor: 'pointer',
    outline: 'none',
    height: buttonHeight,
    textAlign: 'left',
    boxShadow: 'none',
    position: 'relative',
    transition: 'transform 0.18s ease, border-color 0.18s ease',
  };

  const dropdownMenuBaseStyle = {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: 0,
    minWidth: '100%',
    background: premiumPanelGradient,
    border: premiumPanelBorder,
    borderRadius: 14,
    padding: 7,
    boxShadow: premiumPanelShadow,
    zIndex: 2100,
    backdropFilter: 'blur(10px)',
  };

  const popoverBaseStyle = {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: 0,
    background: premiumPanelGradient,
    border: premiumPanelBorder,
    borderRadius: 14,
    padding: 14,
    zIndex: 2000,
    boxShadow: premiumPanelShadow,
    backdropFilter: 'blur(10px)',
  };
  const premiumTitleStyle = {
    color: safeTextTertiary,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.35,
    marginBottom: 10,
  };
  const premiumInputStyle = {
    width: '100%',
    background: hexToRgba(safeBgSecondary, 0.9, safeBgSecondary),
    border: 'none',
    borderRadius: 8,
    color: safeTextPrimary,
    fontSize: 12,
    padding: '8px 10px',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const primaryBtnStyle = {
    background: hexToRgba(safeAccent, 0.24, safeAccent),
    border: 'none',
    borderRadius: 8,
    color: safeTextTertiary,
    fontSize: 12,
    fontWeight: 700,
    padding: '7px 11px',
    cursor: 'pointer',
    boxShadow: 'none',
    transition: 'transform 0.2s ease, border-color 0.2s ease, background 0.2s ease',
  };
  const ghostBtnStyle = {
    background: hexToRgba(safeBgSecondary, 0.88, safeBgSecondary),
    border: 'none',
    borderRadius: 8,
    color: safeTextTertiary,
    fontSize: 12,
    padding: '7px 11px',
    cursor: 'pointer',
    boxShadow: 'none',
    transition: 'transform 0.2s ease, border-color 0.2s ease, color 0.2s ease',
  };

  const textColors = useMemo(() => ([
    safeTextPrimary,
    safeTextSecondary,
    safeTextTertiary,
    safeAccent,
    safeVarkVisual,
    safeVarkAuditory,
    safeVarkReading,
    safeVarkKinesthetic,
    safeSuccess,
    safeWarning,
    safeError,
    hexToRgba(safeTextPrimary, 0.78, safeTextPrimary),
    hexToRgba(safeTextSecondary, 0.92, safeTextSecondary),
    hexToRgba(safeAccent, 0.82, safeAccent),
    hexToRgba(safeSuccess, 0.82, safeSuccess),
    hexToRgba(safeWarning, 0.82, safeWarning),
  ]), [
    safeTextPrimary,
    safeTextSecondary,
    safeTextTertiary,
    safeAccent,
    safeVarkVisual,
    safeVarkAuditory,
    safeVarkReading,
    safeVarkKinesthetic,
    safeSuccess,
    safeWarning,
    safeError,
  ]);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showStyleMenu,   setShowStyleMenu]   = useState(false);
  const [showFontMenu,    setShowFontMenu]    = useState(false);
  const [styleTriggerHover, setStyleTriggerHover] = useState(false);
  const [fontTriggerHover, setFontTriggerHover] = useState(false);
  const [showMathInput,   setShowMathInput]   = useState(false);
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [mathFormula,     setMathFormula]     = useState('');
  const [tableRows,       setTableRows]       = useState(3);
  const [tableCols,       setTableCols]       = useState(3);
  const [selectedMathPos, setSelectedMathPos] = useState(null);
  const [selectedMathRaw, setSelectedMathRaw] = useState('');
  const [editorVersion, forceUpdate]          = useState(0);

  const colorRef = useRef(null);
  const styleRef = useRef(null);
  const fontRef  = useRef(null);
  const mathRef  = useRef(null);
  const tableRef = useRef(null);
  const imageRef     = useRef(null);
  const imageFileRef = useRef(null);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imageUrl,        setImageUrl]        = useState('');
  const [fontSizeInput,   setFontSizeInput]   = useState('');
  const [mathFontSize, setMathFontSize] = useState(31.5);
  const [mathFontSizeInput, setMathFontSizeInput] = useState('32');
  const [mathFieldFocused, setMathFieldFocused] = useState(false);
  const mathFieldRef = useRef(null);
  const syncMathFieldRef = useRef(false);
  const skipNextMathFieldSyncRef = useRef(false);

  const readMathFieldLatex = useCallback((mathField, fallback = '') => {
    if (!mathField) return String(fallback || '');
    if (Object.prototype.hasOwnProperty.call(mathField, 'isConnected') && mathField.isConnected === false) {
      return String(fallback || '');
    }

    try {
      if (typeof mathField.value === 'string') {
        return mathField.value;
      }
    } catch (_error) {
      // Ignore transient MathLive states and fallback below.
    }

    try {
      if (typeof mathField.getValue === 'function') {
        return String(mathField.getValue('latex') || '');
      }
    } catch (_error) {
      // MathLive can throw here during focus/blur transitions on selection.
    }

    return String(fallback || '');
  }, []);

  const TABLE_PICKER_MAX = 8;

  // pour que les états actifs (gras, italique, etc.) restent synchronisés
  useEffect(() => {
    if (!editor) return;
    const syncMathSelection = () => {
      const { selection } = editor.state;
      const selectedNode = selection?.node;

      if (selectedNode?.type?.name === 'mathInline') {
        const latex = selectedNode.attrs?.latex || '';
        const nodeFontSize = Number.isFinite(selectedNode.attrs?.fontSize)
          ? selectedNode.attrs.fontSize
          : 31.5;
        setSelectedMathPos(selection.from);
        setSelectedMathRaw(latex);
        setMathFormula(latex);
        setMathFontSize(nodeFontSize);
        setMathFontSizeInput(String(Math.round(nodeFontSize)));
        setShowMathInput(true);
      } else {
        setSelectedMathPos(null);
        setSelectedMathRaw('');
      }
    };

    const onUpdate = () => {
      forceUpdate((n) => n + 1);
      syncMathSelection();
    };

    syncMathSelection();
    editor.on('update',          onUpdate);
    editor.on('selectionUpdate', onUpdate);
    return () => {
      editor.off('update',          onUpdate);
      editor.off('selectionUpdate', onUpdate);
    };
  }, [editor]);

  // Fermer les popovers au clic extérieur
  useEffect(() => {
    const handleOutside = (e) => {
      const target = e?.target;
      if (target instanceof HTMLElement) {
        const mathNode = target.closest('[data-type="math-inline"]');
        const editorDom = editor?.view?.dom;
        if (mathNode && editorDom && editorDom.contains(mathNode)) {
          return;
        }
      }

      if (styleRef.current && !styleRef.current.contains(e.target)) setShowStyleMenu(false);
      if (fontRef.current  && !fontRef.current.contains(e.target))  setShowFontMenu(false);
      if (colorRef.current && !colorRef.current.contains(e.target)) setShowColorPicker(false);
      if (mathRef.current  && !mathRef.current.contains(e.target))  setShowMathInput(false);
      if (tableRef.current && !tableRef.current.contains(e.target)) setShowTablePicker(false);
      if (imageRef.current && !imageRef.current.contains(e.target)) setShowImagePicker(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [editor]);

  useEffect(() => {
    if (!editor) {
      setFontSizeInput('');
      return;
    }
    const fontSize = editor.getAttributes('textStyle')?.fontSize;
    const numeric = parseInt(String(fontSize || '').replace('px', ''), 10);
    setFontSizeInput(Number.isFinite(numeric) ? String(numeric) : '');
  }, [editor, editorVersion]);

  // ── Style courant ────────────────────────────────────────────────────────────
  const getCurrentStyle = () => {
    for (let level = 1; level <= 6; level++) {
      if (editor.isActive('heading', { level })) return `h${level}`;
    }
    return 'paragraph';
  };

  const handleStyleValueChange = (val) => {
    if (val === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else {
      editor.chain().focus().toggleHeading({ level: Number(val[1]) }).run();
    }
    setShowStyleMenu(false);
  };

  const normalizeFamily = (value) => String(value || '')
    .toLowerCase()
    .replace(/["']/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const getPrimaryFamilyToken = (value) => {
    const normalized = normalizeFamily(value);
    if (!normalized) return '';
    return normalized.split(',')[0].trim();
  };

  const getCurrentFontFamily = () => {
    const family = editor.getAttributes('textStyle')?.fontFamily;
    return typeof family === 'string' ? family : '';
  };

  const getCurrentFontFamilyValue = () => {
    const currentToken = getPrimaryFamilyToken(getCurrentFontFamily());
    if (!currentToken) return FONT_FAMILY_OPTIONS[0].value;

    const found = FONT_FAMILY_OPTIONS.find((opt) => {
      return getPrimaryFamilyToken(opt.value) === currentToken;
    });

    return found ? found.value : FONT_FAMILY_OPTIONS[0].value;
  };

  const handleFontFamilyValueChange = (val) => {
    // Always apply on whole document so the visual change is immediate.
    if (typeof editor.commands.setFontFamily === 'function') {
      editor.chain().focus().selectAll().setFontFamily(val).focus('end').setFontFamily(val).run();
    } else {
      editor.chain().focus().selectAll().setMark('textStyle', { fontFamily: val }).focus('end').setMark('textStyle', { fontFamily: val }).run();
    }
    setShowFontMenu(false);
  };

  const getCurrentFontSize = () => {
    const fontSize = editor.getAttributes('textStyle')?.fontSize;
    return typeof fontSize === 'string' ? fontSize : '';
  };

  const normalizeFontSizeValue = (raw) => {
    const parsed = parseInt(String(raw || '').replace(/[^0-9]/g, ''), 10);
    if (!Number.isFinite(parsed)) return '';
    const clamped = Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, parsed));
    return `${clamped}px`;
  };

  const applyFontSize = (value) => {
    if (!value) {
      if (typeof editor.commands.unsetFontSize === 'function') {
        editor.chain().focus().unsetFontSize().run();
      } else {
        editor.chain().focus().setMark('textStyle', { fontSize: null }).run();
      }
      return;
    }

    if (typeof editor.commands.setFontSize === 'function') {
      editor.chain().focus().setFontSize(value).run();
    } else {
      editor.chain().focus().setMark('textStyle', { fontSize: value }).run();
    }
  };

  const stepFontSize = (delta) => {
    const current = parseInt((getCurrentFontSize() || '').replace('px', ''), 10);
    const fallback = 16;
    const next = Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, (Number.isFinite(current) ? current : fallback) + delta));
    const cssValue = `${next}px`;
    setFontSizeInput(String(next));
    applyFontSize(cssValue);
  };

  const commitManualFontSize = () => {
    const cssValue = normalizeFontSizeValue(fontSizeInput);
    if (!cssValue) {
      setFontSizeInput('');
      applyFontSize('');
      return;
    }
    setFontSizeInput(cssValue.replace('px', ''));
    applyFontSize(cssValue);
  };

  const normalizeMathFontSizeValue = (raw) => {
    const parsed = parseInt(String(raw || '').replace(/[^0-9]/g, ''), 10);
    if (!Number.isFinite(parsed)) return null;
    return Math.min(MATH_FONT_SIZE_MAX, Math.max(MATH_FONT_SIZE_MIN, parsed));
  };

  const stepMathFontSize = (delta) => {
    const next = Math.min(MATH_FONT_SIZE_MAX, Math.max(MATH_FONT_SIZE_MIN, Math.round(mathFontSize) + delta));
    setMathFontSize(next);
    setMathFontSizeInput(String(next));
  };

  const commitManualMathFontSize = () => {
    const next = normalizeMathFontSizeValue(mathFontSizeInput);
    if (!next) {
      setMathFontSizeInput(String(Math.round(mathFontSize)));
      return;
    }
    setMathFontSize(next);
    setMathFontSizeInput(String(next));
  };

  // ── Insertion formule mathématique ───────────────────────────────────────────
  const insertMath = () => {
    const formula = mathFormula.trim();
    if (!formula) return;

    // Si une formule est sélectionnée, on la modifie directement.
    if (
      selectedMathPos !== null
      && selectedMathRaw !== ''
      && typeof editor?.commands?.updateMathInline === 'function'
    ) {
      editor.chain().focus().updateMathInline(formula, { fontSize: parseFloat(String(mathFontSize)) }).run();
      setSelectedMathRaw(formula);
    } else if (typeof editor?.commands?.insertMathInline === 'function') {
      editor.chain().focus().insertMathInline(formula, { fontSize: parseFloat(String(mathFontSize)) }).run();
    } else {
      editor.chain().focus().insertContent('$' + formula + '$').run();
    }

    setMathFormula('');
    setSelectedMathPos(null);
    setSelectedMathRaw('');
    setShowMathInput(false);
  };

  const appendMathToken = (snippet) => {
    setMathFormula((prev) => {
      if (!prev.trim()) return snippet;
      return `${prev} ${snippet}`;
    });
  };

  const insertMathTokenAtCursor = (snippet) => {
    const mathField = mathFieldRef.current;
    if (!mathField) {
      appendMathToken(snippet);
      return;
    }

    syncMathFieldRef.current = true;
    if (typeof mathField.insert === 'function') {
      mathField.insert(snippet, { insertionMode: 'insertAfter', selectionMode: 'placeholder' });
    } else if (typeof mathField.executeCommand === 'function') {
      mathField.executeCommand(['insert', snippet]);
    } else {
      const next = `${mathFormula || ''}${mathFormula ? ' ' : ''}${snippet}`;
      mathField.value = next;
    }
    const value = readMathFieldLatex(mathField, mathFormula);
    syncMathFieldRef.current = false;
    setMathFormula(value);
  };

  useEffect(() => {
    if (!showMathInput || !mathFieldRef.current) return;
    const mathField = mathFieldRef.current;
    let keyboardToggleObserver = null;

    if ('mathVirtualKeyboardPolicy' in mathField) {
      mathField.mathVirtualKeyboardPolicy = 'manual';
    }

    if ('smartMode' in mathField) {
      mathField.smartMode = true;
    }

    if ('readOnly' in mathField) {
      mathField.readOnly = false;
    }

    if (typeof window !== 'undefined' && window.mathVirtualKeyboard && typeof window.mathVirtualKeyboard === 'object') {
      window.mathVirtualKeyboard.layouts = [];
    }

    // Force-hide virtual keyboard toggles created inside MathLive shadow DOM.
    const hideVirtualKeyboardToggle = () => {
      const root = mathField.shadowRoot;
      if (!root) return;

      const selectors = [
        '.ML__virtual-keyboard-toggle',
        '[part="virtual-keyboard-toggle"]',
        '[part*="virtual-keyboard-toggle"]',
        'button[aria-label*="keyboard" i]',
      ];

      root.querySelectorAll(selectors.join(',')).forEach((element) => {
        element.style.display = 'none';
        element.style.visibility = 'hidden';
        element.style.opacity = '0';
        element.style.pointerEvents = 'none';
      });
    };

    hideVirtualKeyboardToggle();

    if (mathField.shadowRoot && typeof MutationObserver !== 'undefined') {
      keyboardToggleObserver = new MutationObserver(() => {
        hideVirtualKeyboardToggle();
      });
      keyboardToggleObserver.observe(mathField.shadowRoot, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    // Avoid forcing focus immediately on mount: MathLive can throw during
    // focus/blur transitions when the field is opened from a selected formula.

    const handleMathInput = () => {
      if (syncMathFieldRef.current) return;
      const value = readMathFieldLatex(mathField, mathFormula);
      // Prevent the mirror effect below from resetting caret/selection after user clicks or types.
      skipNextMathFieldSyncRef.current = true;
      setMathFormula(value);
    };

    const handleFocusIn = () => setMathFieldFocused(true);
    const handleFocusOut = () => setMathFieldFocused(false);

    mathField.addEventListener('input', handleMathInput);
    mathField.addEventListener('focusin', handleFocusIn);
    mathField.addEventListener('focusout', handleFocusOut);
    return () => {
      mathField.removeEventListener('input', handleMathInput);
      mathField.removeEventListener('focusin', handleFocusIn);
      mathField.removeEventListener('focusout', handleFocusOut);
      if (keyboardToggleObserver) keyboardToggleObserver.disconnect();
    };
  }, [showMathInput, readMathFieldLatex, mathFormula]);

  useEffect(() => {
    if (!showMathInput || !mathFieldRef.current) return;

    // While MathLive is focused, do not mirror external state back into the field,
    // otherwise setValue() resets the caret position on click.
    if (mathFieldFocused) return;

    if (skipNextMathFieldSyncRef.current) {
      skipNextMathFieldSyncRef.current = false;
      return;
    }

    const mathField = mathFieldRef.current;
    const current = readMathFieldLatex(mathField, mathFormula);

    if (current === (mathFormula || '')) return;

    syncMathFieldRef.current = true;
    if (typeof mathField.setValue === 'function') {
      mathField.setValue(mathFormula || '', { silenceNotifications: true });
    } else {
      mathField.value = mathFormula || '';
    }
    syncMathFieldRef.current = false;
  }, [mathFormula, showMathInput, mathFieldFocused, readMathFieldLatex]);

  // ── Insertion tableau selon taille choisie ───────────────────────────────────
  // ── Insertion image depuis URL ────────────────────────────────────────────────
  const insertImageFromUrl = () => {
    const url = imageUrl.trim();
    if (!url) return;
    // Accepter http://, https:// et data: uniquement
    if (!/^(https?:|data:image\/)/.test(url)) return;
    editor.chain().focus().setImage({ src: url }).run();
    setShowImagePicker(false);
    setImageUrl('');
  };

  const insertTable = (rows, cols) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    setShowTablePicker(false);
  };

  const handleTableHover = (rows, cols) => {
    setTableRows(rows);
    setTableCols(cols);
  };

  if (!editor) return null;

  const currentStyle = getCurrentStyle();
  const currentFontFamily = getCurrentFontFamilyValue();
  const currentFontFamilyLabel = FONT_FAMILY_OPTIONS.find((o) => o.value === currentFontFamily)?.label || 'Inter';

  return (
    <div
      className={['plynote-toolbar-scroll', className].filter(Boolean).join(' ')}
      style={{
        width: toolbarWidth,
        maxWidth: '100%',
        minHeight: height,
        background: toolbarBg,
        borderBottom: 'none',
        borderRadius: 0,
        position: 'relative',
        zIndex: 30,
        userSelect: 'none',
        margin: 0,
        boxShadow: 'none',
        border: `1px solid ${hexToRgba(safeTextPrimary, 0.12, safeTextPrimary)}`,
        overflow: 'visible',
        WebkitOverflowScrolling: 'touch',
        scrollbarGutter: 'stable',
        ...style,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: gap,
          padding: `${paddingVertical}px ${paddingHorizontal}px`,
          minHeight: '100%',
          minWidth: 0,
          width: '100%',
          justifyContent: 'flex-start',
          alignContent: 'center',
        }}
      >

      {/* ─── 1. Contrôles style + taille (ligne compacte) ─────────────────── */}
      <div ref={styleRef} style={{ position: 'relative', minWidth: selectMinWidth + 8 }}>
        <button
          type="button"
          onClick={() => setShowStyleMenu((v) => !v)}
          onMouseEnter={() => setStyleTriggerHover(true)}
          onMouseLeave={() => setStyleTriggerHover(false)}
          style={{
            ...dropdownTriggerBaseStyle,
            transform: styleTriggerHover ? 'translateY(-1px)' : 'translateY(0)',
            borderColor: styleTriggerHover ? hexToRgba(safeAccent, 0.65, safeAccent) : hexToRgba(safeAccent, 0.46, safeAccent),
            background: styleTriggerHover ? hexToRgba(safeTextPrimary, 0.14, safeTextPrimary) : hexToRgba(safeTextPrimary, 0.1, safeTextPrimary),
            boxShadow: dropdownTriggerBaseStyle.boxShadow,
          }}
          title="Choose a style"
        >
          {STYLE_OPTIONS.find((o) => o.value === currentStyle)?.label || 'Normal'}
          <span
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: `translateY(-50%) rotate(${showStyleMenu ? 180 : 0}deg) scale(${styleTriggerHover ? 1.08 : 1})`,
              color: safeTextTertiary,
              fontSize: 12,
              transition: 'transform 0.22s ease, color 0.18s ease, filter 0.2s ease',
              filter: 'none',
              pointerEvents: 'none',
            }}
          >
            <FiChevronDown size={10} />
          </span>
        </button>

        {showStyleMenu && (
          <div style={dropdownMenuBaseStyle}>
            {STYLE_OPTIONS.map((option) => {
              const isActive = option.value === currentStyle;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleStyleValueChange(option.value)}
                  onMouseEnter={(e) => {
                    if (isActive) return;
                    e.currentTarget.style.transform = 'translateX(2px)';
                    e.currentTarget.style.color = safeTextPrimary;
                  }}
                  onMouseLeave={(e) => {
                    if (isActive) return;
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.color = safeTextSecondary;
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    borderRadius: 0,
                    border: 'none',
                    background: 'transparent',
                    color: isActive ? safeTextPrimary : safeTextSecondary,
                    fontSize: 12,
                    fontWeight: isActive ? 700 : 500,
                    cursor: 'pointer',
                    transition: 'color 0.17s ease, transform 0.17s ease',
                    boxShadow: 'none',
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div ref={fontRef} style={{ position: 'relative', minWidth: selectMinWidth + 8 }}>
        <button
          type="button"
          onClick={() => setShowFontMenu((v) => !v)}
          onMouseEnter={() => setFontTriggerHover(true)}
          onMouseLeave={() => setFontTriggerHover(false)}
          style={{
            ...dropdownTriggerBaseStyle,
            transform: fontTriggerHover ? 'translateY(-1px)' : 'translateY(0)',
            borderColor: fontTriggerHover ? hexToRgba(safeAccent, 0.65, safeAccent) : hexToRgba(safeAccent, 0.46, safeAccent),
            background: fontTriggerHover ? hexToRgba(safeTextPrimary, 0.14, safeTextPrimary) : hexToRgba(safeTextPrimary, 0.1, safeTextPrimary),
            boxShadow: dropdownTriggerBaseStyle.boxShadow,
          }}
          title="Choose a font"
          aria-label={`Police actuelle: ${currentFontFamilyLabel}`}
        >
          <span style={{ fontFamily: currentFontFamily }}>
            {currentFontFamilyLabel}
          </span>
          <span
            style={{
              position: 'absolute',
              right: 10,
              top: '50%',
              transform: `translateY(-50%) rotate(${showFontMenu ? 180 : 0}deg) scale(${fontTriggerHover ? 1.08 : 1})`,
              color: safeTextTertiary,
              fontSize: 12,
              transition: 'transform 0.22s ease, color 0.18s ease, filter 0.2s ease',
              filter: 'none',
              pointerEvents: 'none',
            }}
          >
            <FiChevronDown size={10} />
          </span>
        </button>

        {showFontMenu && (
          <div style={dropdownMenuBaseStyle}>
            {FONT_FAMILY_OPTIONS.map((option) => {
              const isActive = option.value === currentFontFamily;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleFontFamilyValueChange(option.value)}
                  onMouseEnter={(e) => {
                    if (isActive) return;
                    e.currentTarget.style.transform = 'translateX(2px)';
                    e.currentTarget.style.color = safeTextPrimary;
                  }}
                  onMouseLeave={(e) => {
                    if (isActive) return;
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.color = safeTextSecondary;
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    borderRadius: 0,
                    border: 'none',
                    background: 'transparent',
                    color: isActive ? safeTextPrimary : safeTextSecondary,
                    fontSize: 12,
                    fontWeight: isActive ? 700 : 500,
                    cursor: 'pointer',
                    transition: 'color 0.17s ease, transform 0.17s ease',
                    fontFamily: option.value,
                    boxShadow: 'none',
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <ToolbarButton
        title="Decrease size"
        onClick={() => stepFontSize(-1)}
        extraStyle={{ width: 28, height: 28, borderRadius: 8 }}
      >
        <FiMinus size={13} />
      </ToolbarButton>

      <div style={{ position: 'relative', width: 64, height: 28, flexShrink: 0 }}>
        <input
          type="text"
          value={fontSizeInput}
          onChange={(e) => setFontSizeInput(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={commitManualFontSize}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitManualFontSize();
            }
          }}
          placeholder="16"
          style={{
            width: '100%',
            height: '100%',
            textAlign: 'left',
            padding: '0 26px 0 10px',
            background: hexToRgba(safeBgSecondary, 0.9, safeBgSecondary),
            border: `1px solid ${hexToRgba(safeAccent, 0.3, safeAccent)}`,
            borderRadius: 8,
            color: safeTextPrimary,
            fontSize: 12,
            outline: 'none',
            boxSizing: 'border-box',
            boxShadow: 'none',
          }}
        />
        <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: safeTextTertiary, fontSize: 11, pointerEvents: 'none' }}>px</span>
      </div>

      <ToolbarButton
        title="Increase size"
        onClick={() => stepFontSize(1)}
        extraStyle={{ width: 28, height: 28, borderRadius: 8 }}
      >
        <FiPlus size={13} />
      </ToolbarButton>

      {/* ─── 5. Équation mathématique ─────────────────────────────────────── */}
      <div ref={mathRef} style={{ position: 'relative' }}>
        <ToolbarButton
          onClick={() => {
            setShowMathInput((v) => {
              const next = !v;
              if (next && selectedMathRaw) {
                setMathFormula(selectedMathRaw);
              }
              if (next && !selectedMathRaw) {
                setMathFormula('');
              }
              return next;
            });
          }}
          active={showMathInput}
          title="Insert equation (Σ)"
        >
          <span style={{
            fontFamily: 'Georgia, serif',
            fontSize: 17,
            fontWeight: 600,
            lineHeight: 1,
          }}>
            Σ
          </span>
        </ToolbarButton>

        {showMathInput && (
          <div style={{ ...popoverBaseStyle, width: 620, minWidth: 620, maxWidth: 620 }}>
            <div style={premiumTitleStyle}>
              Live LaTeX Editor
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 8,
              marginBottom: 10,
            }}>
              <span style={{ color: safeTextTertiary, fontSize: 11, fontWeight: 700, letterSpacing: 0.35, textTransform: 'uppercase' }}>
                Formula Size
              </span>

              <ToolbarButton
                title="Decrease formula size"
                onClick={() => stepMathFontSize(-1)}
                extraStyle={{ width: 28, height: 28, borderRadius: 8 }}
              >
                <FiMinus size={13} />
              </ToolbarButton>

              <div style={{ position: 'relative', width: 68, height: 28, flexShrink: 0 }}>
                <input
                  type="text"
                  value={mathFontSizeInput}
                  onChange={(e) => setMathFontSizeInput(e.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={commitManualMathFontSize}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitManualMathFontSize();
                    }
                  }}
                  placeholder="32"
                  style={{
                    width: '100%',
                    height: '100%',
                    textAlign: 'left',
                    padding: '0 26px 0 10px',
                    background: hexToRgba(safeBgSecondary, 0.9, safeBgSecondary),
                    border: `1px solid ${hexToRgba(safeAccent, 0.3, safeAccent)}`,
                    borderRadius: 8,
                    color: safeTextPrimary,
                    fontSize: 12,
                    outline: 'none',
                    boxSizing: 'border-box',
                    boxShadow: 'none',
                  }}
                />
                <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: safeTextTertiary, fontSize: 11, pointerEvents: 'none' }}>px</span>
              </div>

              <ToolbarButton
                title="Increase formula size"
                onClick={() => stepMathFontSize(1)}
                extraStyle={{ width: 28, height: 28, borderRadius: 8 }}
              >
                <FiPlus size={13} />
              </ToolbarButton>
            </div>

            <div style={{ position: 'relative', marginBottom: 10 }}>
              {!mathFormula.trim() && !mathFieldFocused && (
                <span
                  style={{
                    position: 'absolute',
                    left: 16,
                    top: 12,
                    color: hexToRgba(safeTextSecondary, 0.72, safeTextSecondary),
                    fontSize: 18,
                    fontStyle: 'normal',
                    fontFamily: '"Segoe UI", "Trebuchet MS", Arial, sans-serif',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    zIndex: 1,
                  }}
                >
                  Type your formula here
                </span>
              )}

              <math-field
                ref={mathFieldRef}
                className="plynote-math-field"
                style={{
                  width: '100%',
                  minHeight: 144,
                  height: 'auto',
                  padding: '12px 16px',
                  borderRadius: 10,
                  border: `1px solid ${hexToRgba(safeTextPrimary, 0.22, safeTextPrimary)}`,
                  background: hexToRgba(safeBgSecondary, 0.82, safeBgSecondary),
                  color: safeTextPrimary,
                  outline: 'none',
                  fontSize: mathFontSize,
                  lineHeight: 1.5,
                  boxSizing: 'border-box',
                  boxShadow: 'none',
                  transition: 'border-color 0.2s ease, background 0.2s ease',
                  '--plynote-math-field-border': hexToRgba(safeTextPrimary, 0.22, safeTextPrimary),
                  '--plynote-math-field-bg': hexToRgba(safeBgSecondary, 0.82, safeBgSecondary),
                  '--plynote-math-field-text': safeTextPrimary,
                }}
              />
            </div>

            {/* Rectangle LaTeX temps réel */}
            <input
              type="text"
              value={mathFormula}
              onChange={(e) => setMathFormula(e.target.value)}
              placeholder="Or insert lattex"
              spellCheck={false}
              style={{
                width: '100%',
                minHeight: 0,
                height: 'calc((1/3) * 144px)',
                maxHeight: 120,
                padding: '12px 16px',
                borderRadius: 10,
                border: `1px solid ${hexToRgba(safeTextPrimary, 0.22, safeTextPrimary)}`,
                background: hexToRgba(safeBgSecondary, 0.82, safeBgSecondary),
                color: safeTextPrimary,
                outline: 'none',
                fontSize: 14,
                lineHeight: 1.5,
                boxSizing: 'border-box',
                boxShadow: 'none',
                transition: 'border-color 0.2s ease, background 0.2s ease',
                marginBottom: 10,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'clip',
              }}
            />

            <div style={{
              marginTop: 8,
              maxHeight: 250,
              overflowY: 'auto',
              paddingRight: 2,
              '--plynote-math-thumb-idle': toolbarBg,
              '--plynote-math-thumb-active': safeAccent,
            }} className="plynote-math-scroll">
              {MATH_SYMBOL_GROUPS.map((group) => (
                <div key={group.title} style={{ marginBottom: 10 }}>
                  <div style={{
                    color: safeTextTertiary,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    marginBottom: 6,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '3px 8px',
                    borderRadius: 999,
                    background: hexToRgba(safeTextPrimary, 0.1, safeTextPrimary),
                    border: `1px solid ${hexToRgba(safeAccent, 0.3, safeAccent)}`,
                  }}>
                    {group.title}
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
                    gap: 6,
                  }}>
                    {group.items.map((item) => (
                      <button
                        key={`${group.title}-${item.display}-${item.latex}`}
                        type="button"
                        onClick={() => insertMathTokenAtCursor(item.latex)}
                        title={item.latex}
                        style={{
                          background: hexToRgba(safeTextPrimary, 0.1, safeTextPrimary),
                          border: `1px solid ${hexToRgba(safeTextSecondary, 0.34, safeTextSecondary)}`,
                          borderRadius: 10,
                          color: safeTextPrimary,
                          fontSize: 13,
                          fontWeight: 700,
                          padding: '6px 5px',
                          cursor: 'pointer',
                          minHeight: 32,
                          lineHeight: 1,
                          boxShadow: 'none',
                          transition: 'transform 0.12s ease, border-color 0.12s ease, background 0.12s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.56, safeAccent);
                          e.currentTarget.style.background = hexToRgba(safeTextPrimary, 0.14, safeTextPrimary);
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = hexToRgba(safeTextSecondary, 0.34, safeTextSecondary);
                          e.currentTarget.style.background = hexToRgba(safeTextPrimary, 0.1, safeTextPrimary);
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {item.display}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                type="button"
                onClick={insertMath}
                style={{
                  flex: 1,
                  ...primaryBtnStyle,
                  cursor: mathFormula.trim() ? 'pointer' : 'not-allowed',
                  opacity: mathFormula.trim() ? 1 : 0.5,
                }}
                onMouseEnter={(e) => {
                  if (!mathFormula.trim()) return;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.82, safeAccent);
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.62, safeAccent);
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {selectedMathRaw ? 'Update' : 'Insert'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowMathInput(false);
                  setMathFormula('');
                  setSelectedMathPos(null);
                  setSelectedMathRaw('');
                }}
                style={{
                  ...ghostBtnStyle,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.4, safeAccent);
                  e.currentTarget.style.color = safeTextPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = hexToRgba(safeTextSecondary, 0.4, safeTextSecondary);
                  e.currentTarget.style.color = safeTextSecondary;
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <Sep />

      {/* ─── 2. Gras / Italique / Souligné ────────────────────────────────── */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        title="Bold (Ctrl+B)"
      >
        <FiBold size={15} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        title="Italic (Ctrl+I)"
      >
        <FiItalic size={15} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')}
        title="Underline (Ctrl+U)"
      >
        <FiUnderline size={15} />
      </ToolbarButton>

      <Sep />

      {/* ─── 3. Couleur du texte ──────────────────────────────────────────── */}
      <div ref={colorRef} style={{ position: 'relative' }}>
        <ToolbarButton
          onClick={() => setShowColorPicker((v) => !v)}
          active={showColorPicker}
          title="Text color"
        >
          <MdFormatColorText size={18} />
        </ToolbarButton>

        {showColorPicker && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            background: premiumPanelGradient,
            border: premiumPanelBorder,
            borderRadius: 12,
            padding: 11,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 22px)',
            gap: 6,
            zIndex: 2000,
            boxShadow: premiumPanelShadow,
            backdropFilter: 'blur(10px)',
          }}>
            {textColors.map((color) => (
              <button
                key={color}
                type="button"
                title={color}
                onClick={() => {
                  editor.chain().focus().setColor(color).run();
                  setShowColorPicker(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px) scale(1.06)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                style={{
                  width: 22,
                  height: 22,
                  background: color,
                  border: `1px solid ${hexToRgba(safeTextPrimary, 0.2, safeTextPrimary)}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'transform 0.14s ease, box-shadow 0.16s ease',
                }}
              />
            ))}
            <button
              type="button"
              title="Reset color"
              onClick={() => {
                editor.chain().focus().unsetColor().run();
                setShowColorPicker(false);
              }}
              style={{
                gridColumn: '1 / -1',
                marginTop: 6,
                background: hexToRgba(safeTextPrimary, 0.1, safeTextPrimary),
                border: `1px solid ${hexToRgba(safeTextSecondary, 0.42, safeTextSecondary)}`,
                borderRadius: 7,
                color: safeTextSecondary,
                fontSize: 11,
                cursor: 'pointer',
                padding: '5px 7px',
                transition: 'transform 0.18s ease, border-color 0.18s ease, color 0.18s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.5, safeAccent);
                e.currentTarget.style.color = safeTextPrimary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = hexToRgba(safeTextSecondary, 0.42, safeTextSecondary);
                e.currentTarget.style.color = safeTextSecondary;
              }}
            >
              Reset
            </button>
          </div>
        )}
      </div>

      <Sep />

      {/* ─── 4. Annuler / Rétablir ────────────────────────────────────────── */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (Ctrl+Z)"
      >
        <MdUndo size={19} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (Ctrl+Y)"
      >
        <MdRedo size={19} />
      </ToolbarButton>

      <Sep />

      {/* ─── 6. Tableau ───────────────────────────────────────────────────── */}
      <div ref={tableRef} style={{ position: 'relative' }}>
        <ToolbarButton
          onClick={() => {
            setShowTablePicker((v) => !v);
            setTableRows(3);
            setTableCols(3);
          }}
          active={showTablePicker}
          title="Insert table (custom size)"
        >
          <MdGridOn size={18} />
        </ToolbarButton>

        {showTablePicker && (
          <div style={{ ...popoverBaseStyle, minWidth: 240, padding: 12 }}>
            <div style={premiumTitleStyle}>
              Table size
            </div>

            <div
              onMouseLeave={() => {
                setTableRows(3);
                setTableCols(3);
              }}
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${TABLE_PICKER_MAX}, 18px)`,
                gap: 3,
                marginBottom: 8,
              }}
            >
              {Array.from({ length: TABLE_PICKER_MAX * TABLE_PICKER_MAX }).map((_, index) => {
                const row = Math.floor(index / TABLE_PICKER_MAX) + 1;
                const col = (index % TABLE_PICKER_MAX) + 1;
                const active = row <= tableRows && col <= tableCols;

                return (
                  <button
                    key={`${row}-${col}`}
                    type="button"
                    onMouseEnter={() => handleTableHover(row, col)}
                    onClick={() => insertTable(row, col)}
                    title={`${row} x ${col}`}
                    style={{
                      width: 18,
                      height: 18,
                      padding: 0,
                      borderRadius: 5,
                      border: active ? `1px solid ${safeAccent}` : `1px solid ${hexToRgba(safeTextSecondary, 0.3, safeTextSecondary)}`,
                      background: active ? hexToRgba(safeAccent, 0.3, safeAccent) : hexToRgba(safeBgSecondary, 0.82, safeBgSecondary),
                      cursor: 'pointer',
                    }}
                  />
                );
              })}
            </div>

            <div style={{
              color: safeTextSecondary,
              fontSize: 12,
              marginBottom: 8,
              fontFamily: 'Inter, system-ui, sans-serif',
            }}>
              {tableRows} x {tableCols}
            </div>

            <button
              type="button"
              onClick={() => insertTable(tableRows, tableCols)}
              style={{
                width: '100%',
                ...primaryBtnStyle,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.82, safeAccent);
                e.currentTarget.style.boxShadow = 'none';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.62, safeAccent);
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Insert {tableRows} x {tableCols}
            </button>
          </div>
        )}
      </div>

      <Sep />

      {/* ─── 7. Image ─────────────────────────────────────────────────────── */}
      <div ref={imageRef} style={{ position: 'relative' }}>
        <ToolbarButton
          onClick={() => setShowImagePicker((v) => !v)}
          active={showImagePicker}
          title="Insert image"
        >
          <MdImage size={18} />
        </ToolbarButton>

        {/* Input fichier masqué */}
        <input
          ref={imageFileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
              const src = ev.target?.result;
              if (src) editor.chain().focus().setImage({ src }).run();
              setShowImagePicker(false);
              setImageUrl('');
              e.target.value = '';
            };
            reader.readAsDataURL(file);
          }}
        />

        {showImagePicker && (
          <div style={{ ...popoverBaseStyle, minWidth: 300 }}>
            <div style={premiumTitleStyle}>
              Insert image
            </div>

            {/* Bouton d'upload */}
            <button
              type="button"
              onClick={() => imageFileRef.current?.click()}
              style={{
                width: '100%',
                    background: hexToRgba(safeTextPrimary, 0.1, safeTextPrimary),
                border: `1px dashed ${hexToRgba(safeTextSecondary, 0.46, safeTextSecondary)}`,
                borderRadius: 10,
                color: safeTextTertiary,
                fontSize: 12,
                padding: '10px 0',
                cursor: 'pointer',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <MdImage size={14} />
              Choose a file (jpg, png, gif…)
            </button>

            {/* Séparateur */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1, height: 1, background: hexToRgba(safeTextSecondary, 0.2, safeTextSecondary) }} />
              <span style={{ color: safeTextTertiary, fontSize: 11 }}>ou URL</span>
              <div style={{ flex: 1, height: 1, background: hexToRgba(safeTextSecondary, 0.2, safeTextSecondary) }} />
            </div>

            {/* Champ URL */}
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter')  insertImageFromUrl();
                if (e.key === 'Escape') { setShowImagePicker(false); setImageUrl(''); }
              }}
              placeholder="https://exemple.com/image.png"
              style={{
                ...premiumInputStyle,
                marginBottom: 10,
              }}
            />

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={insertImageFromUrl}
                disabled={!imageUrl.trim()}
                style={{
                  flex: 1,
                  ...primaryBtnStyle,
                  cursor: imageUrl.trim() ? 'pointer' : 'not-allowed',
                  opacity: imageUrl.trim() ? 1 : 0.5,
                }}
                onMouseEnter={(e) => {
                  if (!imageUrl.trim()) return;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.82, safeAccent);
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.62, safeAccent);
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Insert
              </button>
              <button
                type="button"
                onClick={() => { setShowImagePicker(false); setImageUrl(''); }}
                style={{
                  ...ghostBtnStyle,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.4, safeAccent);
                  e.currentTarget.style.color = safeTextPrimary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = hexToRgba(safeTextSecondary, 0.4, safeTextSecondary);
                  e.currentTarget.style.color = safeTextSecondary;
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <Sep />

      {/* ─── 8. Listes ────────────────────────────────────────────────────── */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        title="Bullet list"
      >
        <MdFormatListBulleted size={18} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        title="Numbered list"
      >
        <MdFormatListNumbered size={18} />
      </ToolbarButton>

      <Sep />

      {/* ─── 8. Alignement ───────────────────────────────────────────────── */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        active={editor.isActive({ textAlign: 'left' })}
        title="Align left"
      >
        <MdFormatAlignLeft size={18} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        active={editor.isActive({ textAlign: 'center' })}
        title="Centrer"
      >
        <MdFormatAlignCenter size={18} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        active={editor.isActive({ textAlign: 'right' })}
        title="Align right"
      >
        <MdFormatAlignRight size={18} />
      </ToolbarButton>

      </div>
    </div>
  );
};

export default TextEditorToolsBar;
